import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Storage keys
const TOKEN_KEY = 'jm_token';
const REMEMBER_KEY = 'jm_remember';

/**
 * AuthProvider med valgfri "Husk meg"-funksjon
 * 
 * SIKKERHETSFUNKSJON:
 * - Uten "Husk meg": Bruker sessionStorage (logges ut ved lukking av fane/nettleser)
 * - Med "Husk meg": Bruker localStorage (forblir innlogget i 7 dager)
 * 
 * Brukeren velger selv sikkerhetsnivå ved innlogging.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Sjekk om brukeren valgte "Husk meg" tidligere
  const wasRemembered = localStorage.getItem(REMEMBER_KEY) === 'true';
  
  // Hent token fra riktig storage basert på "Husk meg"-valg
  const getStoredToken = () => {
    if (wasRemembered) {
      return localStorage.getItem(TOKEN_KEY);
    }
    return sessionStorage.getItem(TOKEN_KEY);
  };
  
  const [token, setToken] = useState(getStoredToken());

  const getHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Migrer fra gammel localStorage-token (uten remember-flagg)
  useEffect(() => {
    const oldToken = localStorage.getItem(TOKEN_KEY);
    const hasRememberFlag = localStorage.getItem(REMEMBER_KEY);
    
    // Hvis det finnes en gammel token uten remember-flagg, fjern den
    if (oldToken && !hasRememberFlag) {
      localStorage.removeItem(TOKEN_KEY);
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => { 
          // Token ugyldig - rydd opp begge steder
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REMEMBER_KEY);
          sessionStorage.removeItem(TOKEN_KEY);
          setToken(null); 
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  /**
   * Login med valgfri "Husk meg"
   * @param {string} email 
   * @param {string} password 
   * @param {boolean} rememberMe - Om brukeren skal forbli innlogget
   */
  const login = async (email, password, rememberMe = false) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    
    if (rememberMe) {
      // Lagre i localStorage for persistent innlogging
      localStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.setItem(REMEMBER_KEY, 'true');
      sessionStorage.removeItem(TOKEN_KEY);
    } else {
      // Lagre i sessionStorage for sikker økt
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REMEMBER_KEY);
    }
    
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password, phone, address, invite_code = null, account_type = 'private') => {
    const payload = { name, email, password, phone, address, account_type };
    if (invite_code) payload.invite_code = invite_code;
    const res = await axios.post(`${API}/auth/register`, payload);
    // Nye brukere bruker sessionStorage som standard
    sessionStorage.setItem(TOKEN_KEY, res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    // Rydd opp begge storage-typer
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REMEMBER_KEY);
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    const res = await axios.put(`${API}/auth/profile`, profileData, getHeaders());
    setUser(res.data);
    return res.data;
  };

  const forgotPassword = async (email) => {
    const res = await axios.post(`${API}/auth/forgot-password`, { email });
    return res.data;
  };

  const resetPassword = async (token, newPassword) => {
    const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
    return res.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getHeaders, updateProfile, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
